// Lazy load crypto libraries to improve initial load time
let galxeSDK: typeof import("@galxe-identity-protocol/sdk") | null = null;
let ethersLib: typeof import("ethers") | null = null;

// Lazy load function for crypto libraries
const loadCryptoLibraries = async () => {
  if (!galxeSDK || !ethersLib) {
    const [galxe, ethers] = await Promise.all([
      import("@galxe-identity-protocol/sdk"),
      import("ethers"),
    ]);
    galxeSDK = galxe;
    ethersLib = ethers;
  }
  return { galxeSDK, ethersLib };
};

let isInitialized = false;
const MAINNET_RPC = "https://rpc.mevblocker.io";
let provider: import("ethers").JsonRpcProvider | null = null;

const init = async () => {
  const { galxeSDK, ethersLib } = await loadCryptoLibraries();
  const { babyzk } = galxeSDK;
  const { prepare } = babyzk;
  await prepare();

  if (!provider) {
    const { ethers } = ethersLib;
    provider = new ethers.JsonRpcProvider(MAINNET_RPC);
  }

  isInitialized = true;
};

export const generateIdentityCommitment = async (
  userPalmID: string
): Promise<string> => {
  if (!isInitialized) {
    await init();
  }

  const { galxeSDK } = await loadCryptoLibraries();
  const { user } = galxeSDK;
  const { User } = user;
  const u = new User();
  u.addIdentitySlice({
    domain: "veros",
    identitySecret: BigInt(userPalmID),
    internalNullifier: BigInt(userPalmID),
  });
  return u.getIdentityCommitment("veros")!.toString();
};

export const generateProof = async (
  userPalmID: string,
  credential: string,
  queryStr: string
) => {
  if (!isInitialized) {
    await init();
  }

  const { galxeSDK } = await loadCryptoLibraries();
  const { user } = galxeSDK;
  const { User } = user;
  const mUser = new User();
  mUser.addIdentitySlice({
    domain: "veros",
    identitySecret: BigInt(userPalmID),
    internalNullifier: BigInt(userPalmID),
  });

  // User's process: generating a zk proof to prove some statements about the credential.
  const proof = await proofGenProcess(credential, mUser, queryStr);
  return proof;
};

const proofGenProcess = async (
  credStr: string,
  u: import("@galxe-identity-protocol/sdk").user.User,
  queryStr: string
) => {
  const { galxeSDK, ethersLib } = await loadCryptoLibraries();
  const { credential, credType, errors } = galxeSDK;
  const unwrap = errors.unwrap;

  if (!provider) {
    const { ethers } = ethersLib;
    provider = new ethers.JsonRpcProvider(MAINNET_RPC);
  }

  // unmarshal the credential
  const myCred = unwrap(
    credential.Credential.unmarshal(
      unwrap(credType.createTypeFromSpec(credType.primitiveTypes.scalar)),
      credStr
    )
  );

  // Now issuer can issue a credential to the user.
  // Assuming that the user has received the credential, user can generate a
  // zk proof to prove that he has recently verified his palm.
  // Let's first decide the external nullifier for the proof.
  // const externalNullifier = utils.computeExternalNullifier(
  //   "Parter app's palm verification"
  // );
  // Now we need to fetch the proof generation gadgets. It is explicitly fetched outside the proof generation function
  // because usually, the proof generation gadgets are stored in a remote server, and may be large (3-10MB).
  // It's highly recommended to cache the proof generation gadgets locally.
  // console.log("downloading proof generation gadgets...");
  const { user } = galxeSDK;
  const proofGenGagets = await user.User.fetchProofGenGadgetsByTypeID(
    myCred.header.type,
    provider
  );
  // console.log("proof generation gadgets are downloaded successfully.");
  // Finally, let's generate the proof.
  // Assume that we want to verify that the credential is still valid after 3 days.
  // const expiredAtLowerBound = BigInt(
  //   Math.ceil(new Date().getTime() / 1000) + 3 * 24 * 60 * 60
  // );
  // Do not reveal the credential's actual id, which is the user palm id in this example.
  // const equalCheckId = BigInt(0);
  // Instead, claim to be the userid by partner's choice. It's verifier's
  // responsibility to verify that the pseudonym is who he claims to be
  // after verifying the proof.
  // const pseudonym = BigInt("0xdeadbeef");

  // if we don't care about this pseudonym, just use 0.
  // const pseudonym = 0;

  // Mon Mar 31 2025 16:00:00 GMT+0000
  // const palmScanTimestampLowerBound = 1743436800;

  const proof = await u.genBabyzkProofWithQuery(
    u.getIdentityCommitment("veros")!,
    myCred,
    proofGenGagets,
    queryStr
    //   `
    // {
    //   "conditions": [
    //     {
    //       "identifier": "val",
    //       "operation": "IN",
    //       "value": {
    //         "from": "${palmScanTimestampLowerBound}",
    //         "to": "${MAX_UINT248.toString()}"
    //       }
    //     }
    //   ],
    //   "options": {
    //     "expiredAtLowerBound": "${expiredAtLowerBound}",
    //     "externalNullifier": "${externalNullifier}",
    //     "equalCheckId": "${equalCheckId}",
    //     "pseudonym": "${pseudonym}"
    //   }
    // }
    // `
  );
  return proof;
};
